<template>
  <v-stepper-window-item value="2">
    <v-card-text>
      <v-row>
        <v-col cols="12">
          <v-alert
            v-if="validationErrors.length"
            type="error"
            title="Validation Errors"
            class="mb-4"
          >
            <ul class="mb-0">
              <li v-for="(error, idx) in validationErrors" :key="idx">
                <template v-if="error.sheet">{{ error.sheet }}</template>
                <template v-if="error.row"> Row {{ error.row }}</template>
                <template v-if="error.column"> [{{ error.column }}]</template>
                : {{ error.message || error }}
              </li>
            </ul>
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card
            title="Import Updates"
            subtitle="Upload a modified Excel file to update existing profiles"
            variant="outlined"
          >
            <v-card-text>
              <v-file-input
                ref="fileInputRef"
                accept=".xlsx,.xls"
                label="Choose Excel file"
                prepend-icon="mdi-file-excel"
                :loading="processing"
                :disabled="processing"
                :hint="fileUploadHint"
                :persistent-hint="true"
                @update:model-value="handleFileUpload"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions>
      <v-btn @click="$emit('back')">
        Back
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        :disabled="!isValid"
        @click="$emit('continue')"
      >
        Continue
      </v-btn>
    </v-card-actions>
  </v-stepper-window-item>
</template>

<script setup>
import { ref, computed } from 'vue';
import { TaskSetProfileExcelProcessor } from '@/utils/TaskSetProfileExcelProcessor';

defineProps({
  existingProfiles: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['back', 'continue', 'update:import-result']);

// State
const fileInputRef = ref(null);
const processing = ref(false);
const isValid = ref(false);
const validationErrors = ref([]);
const importResult = ref(null);

const fileUploadHint = computed(() => {
  if (validationErrors.value.length) {
    return 'Validation errors detected. Please check and reupload.';
  }
  return importResult.value ? 'File uploaded successfully' : 'Please upload an Excel file';
});

async function handleFileUpload(files) {
  const file = Array.isArray(files) ? files[0] : files;
  if (!file) {
    resetState();
    return;
  }

  processing.value = true;
  try {
    const buffer = await file.arrayBuffer();
    const processor = new TaskSetProfileExcelProcessor();
    const result = processor.processFile(buffer);

    importResult.value = result;

    if (!result.isValid) {
      isValid.value = false;
      validationErrors.value = result.errors || [];
      return;
    }

    emit('update:import-result', result);
    isValid.value = true;
    validationErrors.value = [];
  } catch (error) {
    console.error('Error processing file:', error);
    validationErrors.value = [{ message: 'Failed to process Excel file: ' + error.message }];
    isValid.value = false;
  } finally {
    processing.value = false;
  }
}

function resetState() {
  importResult.value = null;
  isValid.value = false;
  validationErrors.value = [];
  emit('update:import-result', null);
}
</script>
